import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ArtistLineup.css";

/**
 * Artist Lineup — renders artists (from event.artistLineup) and their ~30s
 * preview tracks, mirroring the mobile app's Event Detail "Artist Lineup" UI.
 *
 * Only one track plays across the whole page at a time: a single shared audio
 * player is created here and passed down to every card and the About modal.
 */

// ---------------------------------------------------------------------------
// Shared audio hook (single active track across the page)
// ---------------------------------------------------------------------------
function useTrackPreviewPlayer() {
  const audioRef = useRef(null);
  const [state, setState] = useState({
    activeUrl: null,
    isPlaying: false,
    isLoading: false,
    progress: 0,
  });

  // lazily create one shared <audio> element
  if (audioRef.current === null && typeof Audio !== "undefined") {
    audioRef.current = new Audio();
  }

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlaying = () => setState((s) => ({ ...s, isPlaying: true, isLoading: false }));
    const onPause = () => setState((s) => ({ ...s, isPlaying: false }));
    const onWaiting = () => setState((s) => ({ ...s, isLoading: true }));
    const onTime = () =>
      setState((s) => ({
        ...s,
        progress: audio.duration ? audio.currentTime / audio.duration : 0,
      }));
    const onEnded = () =>
      setState({ activeUrl: null, isPlaying: false, isLoading: false, progress: 0 });

    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
      // Stop playback when the lineup unmounts (e.g. navigating away).
      audio.pause();
      audio.src = "";
    };
  }, []);

  const toggle = useCallback(
    (previewUrl) => {
      const audio = audioRef.current;
      const url = (previewUrl || "").trim();
      if (!audio || !url) return;

      // same track → pause / resume
      if (state.activeUrl === url) {
        if (audio.paused) audio.play().catch(() => {});
        else audio.pause();
        return;
      }

      // switch track
      audio.pause();
      audio.src = url;
      setState({ activeUrl: url, isPlaying: false, isLoading: true, progress: 0 });
      audio.play().catch(() =>
        setState({ activeUrl: null, isPlaying: false, isLoading: false, progress: 0 })
      );
    },
    [state.activeUrl]
  );

  return { state, toggle };
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------
const PlayIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="6" y="5" width="4" height="14" rx="1" />
    <rect x="14" y="5" width="4" height="14" rx="1" />
  </svg>
);

const LoadingIcon = () => (
  <svg
    className="al-spinner"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
);

// ---------------------------------------------------------------------------
// Progress ring (SVG circle whose strokeDashoffset tracks 0..1)
// ---------------------------------------------------------------------------
function ProgressRing({ value, size = 46, stroke = 2.5, color = "#ffffff" }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, value || 0));
  const offset = circumference * (1 - clamped);
  const center = size / 2;

  return (
    <svg className="al-progress-ring" width={size} height={size} aria-hidden="true">
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${center} ${center})`}
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Track item (artwork + name + play/pause with progress ring)
// ---------------------------------------------------------------------------
function TrackItem({ track, player }) {
  const url = (track.previewUrl || "").trim();
  const canPlay = url.length > 0;
  const isActive = player.state.activeUrl === url && canPlay;
  const showPause = isActive && player.state.isPlaying;
  const showLoading = isActive && player.state.isLoading;
  const progress = isActive ? player.state.progress : 0;

  return (
    <div className="al-track-item">
      <img className="al-track-art" src={track.artworkUrl} alt="" loading="lazy" />
      <span className="al-track-name" title={track.name}>
        {track.name}
      </span>
      <div className="al-play-wrap">
        {isActive && !showLoading && <ProgressRing value={progress} />}
        <button
          type="button"
          className={`al-play-btn${isActive ? " al-play-btn--active" : ""}`}
          disabled={!canPlay}
          onClick={() => canPlay && player.toggle(url)}
          aria-label={!canPlay ? "Preview unavailable" : showPause ? "Pause" : "Play"}
        >
          {showLoading ? <LoadingIcon /> : showPause ? <PauseIcon /> : <PlayIcon />}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Track list ("Most Played" header optional — shown in the modal, hidden on the card)
// ---------------------------------------------------------------------------
function TracksList({ tracks, player, showHeader = true }) {
  return (
    <div className="al-tracks">
      {showHeader && (
        <div className="al-most-played">
          <span className="al-mp-line" />
          <span className="al-mp-label">Most Played</span>
          <span className="al-mp-line" />
        </div>
      )}
      {tracks.map((t, i) => (
        <TrackItem key={t.previewUrl || t.name || i} track={t} player={player} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// About Artist modal
// ---------------------------------------------------------------------------
function AboutArtistModal({ artist, player, onClose }) {
  // Close on Escape
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="al-modal-overlay" onClick={onClose}>
      <div
        className="al-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`About ${artist.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-head">
          <h3 className="al-modal-title">About {artist.name}</h3>
          <button type="button" className="al-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <img className="al-modal-avatar" src={artist.imageUrl} alt={artist.name} />
        <div className="al-modal-name">{artist.name}</div>
        {artist.description && artist.description.trim() && (
          <p className="al-modal-desc">{artist.description}</p>
        )}

        {artist.tracks && artist.tracks.length > 0 && (
          <TracksList tracks={artist.tracks} player={player} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Artist card (wide horizontal layout: avatar + name | tracks)
// ---------------------------------------------------------------------------
function ArtistCard({ artist, player }) {
  const [showAbout, setShowAbout] = useState(false);

  // Match the app: only render when there are tracks.
  if (!artist.tracks || artist.tracks.length === 0) return null;

  return (
    <div className="al-artist-card">
      <button
        type="button"
        className="al-artist-info"
        onClick={() => setShowAbout(true)}
        aria-label={`About ${artist.name}`}
      >
        <img className="al-artist-avatar" src={artist.imageUrl} alt={artist.name} />
        <span className="al-artist-name">{artist.name}</span>
      </button>

      <span className="al-divider" aria-hidden="true" />

      <TracksList tracks={artist.tracks} player={player} showHeader={false} />

      {showAbout && (
        <AboutArtistModal artist={artist} player={player} onClose={() => setShowAbout(false)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lineup (entry point)
// ---------------------------------------------------------------------------
function ArtistLineup({ event }) {
  // One shared player across all cards → only one track plays at a time.
  const player = useTrackPreviewPlayer();

  const artists = (event?.artistLineup ?? []).filter(
    (a) => a && a.tracks && a.tracks.length > 0
  );
  if (artists.length === 0) return null;

  

  return (
    <section className="al-lineup">
      <h2 className="section-title">Artist Lineup</h2>
      <div className="al-cards">
        {artists.map((a) => (
          <ArtistCard key={a.id || a.name} artist={a} player={player} />
        ))}
      </div>
    </section>
  );
}

export default ArtistLineup;
